type TrappedElement = {
  element: HTMLElement;
  firstElement: HTMLElement;
  lastElement: HTMLElement;
};

/**
 * This class was created so that this logic can be reused on other components
 * even if they have different levels of interaction. This might need some tweaks
 * and there are function currently not being used but that will
 * come in handy when/if this is used anywhere else.
 *
 * The 'only' thing that this class does is keep track of focused elements
 * in a predefined order so that we can navigate back and forth between different
 * levels of the same component (e.g Header flyout levels).
 */
export class AccessibilityStackManager {
  public static instance: AccessibilityStackManager;

  private _contextCounter: Record<string, number> = {};
  private _focusStack: HTMLElement[] = [];
  private _trappedElement: TrappedElement | null = null;

  private _keyboardTrapEvent(event: Event): void {
    if (this._trappedElement == null) {
      return;
    }

    const keyboardEvent = event as KeyboardEvent;
    const { firstElement, lastElement } = this._trappedElement;

    if (keyboardEvent.key === 'Tab' && document.activeElement === lastElement) {
      firstElement.focus();
      keyboardEvent.preventDefault();
    } else if (
      keyboardEvent.shiftKey &&
      keyboardEvent.key === 'Tab' &&
      document.activeElement === firstElement
    ) {
      lastElement.focus();
      keyboardEvent.preventDefault();
    }
  }

  private _increaseContextStack(context: string): void {
    if (!this._contextCounter[context]) {
      this._contextCounter[context] = 0;
    }
    this._contextCounter[context] += 1;
  }

  private _decreaseContextStack(context: string, numberOfPops = 0): void {
    if (!this._contextCounter[context]) {
      this._contextCounter[context] = 0;
    } else {
      this._contextCounter[context] -= 1 + numberOfPops;
    }
  }

  public pushToFocusStack(element: HTMLElement, context: string, shouldFocus = true): number {
    if (!element) {
      return -1;
    }

    this._focusStack.push(element);

    if (shouldFocus) {
      element.focus();
    }

    this._increaseContextStack(context);
    return this.getLastFocusIndex();
  }

  public pushToFocusStackOnIndex(index: number, element: HTMLElement, context: string, shouldFocus = true) {
    if (!element) {
      return;
    }
    this._focusStack.length = index;
    this._increaseContextStack(context);
    this.pushToFocusStack(element, context, shouldFocus);
  }

  public popLastFocusedElementOnStack(
    context: string,
    removeIt = true,
    shouldFocus = true,
  ): HTMLElement | undefined {
    if (this._focusStack.length === 0) {
      return document.body;
    }

    const focusedElement = this._focusStack[this._focusStack.length - 1];

    if (shouldFocus) {
      if (this._focusStack.length > 1) {
        this._focusStack[this._focusStack.length - 1].focus();
      } else {
        focusedElement.focus();
      }
    }

    if (removeIt) {
      this._decreaseContextStack(context);
      return this._focusStack.pop();
    }

    return focusedElement;
  }

  public popFocusElementStack(context: string, numberOfPops = 1, shouldFocus = true) {
    if (numberOfPops >= this._focusStack.length) {
      this.reset();
      this._contextCounter[context] = 0;

      return document.body;
    }

    this._focusStack.length = numberOfPops + 1;
    const focusedElement = this._focusStack.pop();

    if (shouldFocus && focusedElement) {
      focusedElement.focus();
    }

    this._decreaseContextStack(context, numberOfPops);
    return focusedElement;
  }

  public getLastFocusIndex() {
    return this._focusStack.length - 1;
  }

  /*
   * WARNING 1: This method will essentially trap the keyboard navigation inside the passed element.
   * Meaning that if not used correctly, it will break the navigation. ALWAYS make sure to call
   * removeKeyboardTrap when the element in question is removed or hidden.
   *
   * WARNING 2: This is not an easy solution to avoid implementing propped keyboard navigation, mainly
   * proper open and close logic. It's very rare for the user to find it helpful to be "trapped".
   * A good example of a good usage of this function is on a full page modal for example.
   * So use these methods sparingly.
   *
   * This method finds the first and last element inside the passed element to find the
   * content boundaries. This way we can check if the tab navigation is about to exit these
   * set boundaries. If that happens we just loop the tab navigation around.
   * It also maintains a reference for the last focus element for when we exit the "trap"
   */
  public setFocusKeyboardTrap(
    attachedElement: Element,
    context: string,
    focusFirstElement = false,
    firstFocusableElement = null,
    lastFocusableElement = null,
  ): void {
    if (attachedElement == null) {
      return;
    }

    let firstElement: Element | null;
    let lastElement: Element | null;

    if (firstFocusableElement == null || lastFocusableElement == null) {
      const focusableElements = attachedElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      firstElement = focusableElements[0];
      lastElement = focusableElements[focusableElements.length - 1];
    } else {
      firstElement = firstFocusableElement;
      lastElement = lastFocusableElement;
    }

    this._trappedElement = {
      element: attachedElement as HTMLElement,
      firstElement: firstElement as HTMLElement,
      lastElement: lastElement as HTMLElement,
    };
    attachedElement.addEventListener('keydown', this._keyboardTrapEvent.bind(this), false);

    this.pushToFocusStack(document.activeElement as HTMLElement, context, false);

    if (focusFirstElement) {
      this._trappedElement.firstElement.focus();
    }
  }

  public removeKeyboardTrap(context: string): void {
    if (this._trappedElement == null) {
      return;
    }

    const { element } = this._trappedElement;

    element?.removeEventListener('keydown', this._keyboardTrapEvent);

    this.popLastFocusedElementOnStack(context);
    this._trappedElement = null;
  }

  public reset(): void {
    this._focusStack.length = 0;
  }

  public resetWithContext(context: string): void {
    const numberOfPops = this._contextCounter[context];

    if (numberOfPops && numberOfPops > 0) {
      this.popFocusElementStack(context, numberOfPops);
    }
  }
}

export default function getAccessibilityStackInstance(): AccessibilityStackManager {
  if (AccessibilityStackManager.instance == null) {
    AccessibilityStackManager.instance = new AccessibilityStackManager();
  }

  return AccessibilityStackManager.instance;
}
